import React, { useContext, useEffect, useState, type ReactElement } from "react"
import Select from "react-select"

import { faCheck, faTrash } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, InlineLoading, TextInput } from "@wfp/ui"

import { mappingOptions, mappingRules } from "../../../../constants/prices"
import type { MappingSchemaRow, SeriesCombination } from "../../../../interfaces/dataInterfaces"

import { MappingPriceContext } from "../../../../providers/MappingPricesContext"

type Props = {
  row: MappingSchemaRow
}

const getDefaultTypeOptions = (dataType: string) => {
  const values = mappingOptions.map(option => option.options.find(opt => opt.value === dataType)).filter(item => !!item)
  return values.length ? values[0] : null
}

const SingleRow = ({ row }: Props): ReactElement => {
  const { schema, mappingRows, onDiscardRow, editMappingRow, onUpdateRow, seriesCombinations } =
    useContext(MappingPriceContext)

  const [loading, setLoading] = useState<boolean>(false)
  const [commodities, setCommodities] = useState<SeriesCombination[]>([])
  const [units, setUnits] = useState<SeriesCombination[]>([])

  const getUnits = (commodityID: number): void => {
    const uniqueCommodityUnits = seriesCombinations
      .filter(item => item.commodityID === commodityID)
      .reduce((acc: SeriesCombination[], curr) => {
        if (!acc.find(item => item.commodityUnitID === curr.commodityUnitID)) {
          acc.push(curr)
        }
        return acc
      }, [])

    setUnits(uniqueCommodityUnits)
  }

  const disabledButton = (): boolean =>
    !row.type ||
    (row?.commodity && row.commodity === -1) ||
    (row?.type === "priceCommodityMeasure" && row.unit === -1) ||
    (row?.type === "metadata" && !row.value)

  useEffect(() => {
    if (seriesCombinations.length) {
      const uniqueCommodities = seriesCombinations.filter(
        (item, index, current) => current.findIndex(el => el.commodityID === item.commodityID) === index,
      )
      setCommodities(uniqueCommodities)
      getUnits(Number(row.commodity))
    }
  }, [seriesCombinations])

  useEffect(() => {
    mappingOptions.map(option => {
      option.options.map(opt => {
        opt.isDisabled = mappingRules[opt.value].validation(opt.value, mappingRows, schema)
      })
    })
  }, [mappingRows, schema])

  return (
    <div className="px-1 border-b border-dotted py-0.5 border-gray-400">
      <div className="flex justify-between w-full">
        <div className="flex items-center w-10/12">
          <div className="flex w-4/12">
            <p className="font-bold">{row.colName}</p>
            <p className="ml-5">{row.cellValue}</p>
          </div>

          <div className="flex w-8/12 gap-3">
            <Select
              key={row?.type}
              placeholder="Select type"
              options={mappingOptions}
              defaultValue={getDefaultTypeOptions(row.dataType)}
              className="w-6/12"
              menuPosition="fixed"
              isClearable
              onChange={item => {
                const newValue = {
                  ...row,
                  type: item?.type ?? "",
                  dataType: item?.value ?? "",
                  metadata: "",
                  value: "",
                  commodity: item?.commodity ? -1 : "",
                  unit: item?.unit ? -1 : "",
                }
                editMappingRow(newValue)
                setUnits([])
              }}
            />

            {typeof row?.commodity === "number" && (
              <Select
                key={row?.dataType}
                placeholder="Commodity"
                options={commodities ?? []}
                className="w-3/12"
                menuPosition="fixed"
                getOptionLabel={option => option.commodityName}
                getOptionValue={option => option.commodityID.toString()}
                value={commodities.find(c => c.commodityID === row.commodity)}
                onChange={item => {
                  editMappingRow({ ...row, commodity: item?.commodityID ?? -1, unit: row?.unit ? -1 : "" })
                  item && getUnits(item.commodityID)
                }}
              />
            )}

            {row.dataType === "priceCommodityMeasure" && (
              <Select
                key={row.commodity}
                placeholder="Unit"
                options={units ?? []}
                getOptionLabel={option => option.commodityUnitName}
                getOptionValue={option => option.commodityUnitID.toString()}
                value={units.find(c => c.commodityUnitID === row.unit)}
                className="w-2/12"
                menuPosition="fixed"
                onChange={item => {
                  editMappingRow({ ...row, unit: item?.commodityUnitID ?? -1 })
                }}
              />
            )}

            {row?.type === "metadata" && (
              <TextInput
                placeholder="Metadata"
                className="w-full"
                value={row.value}
                onChange={item => {
                  editMappingRow({ ...row, value: item?.target?.value ?? "", dataType: "metadata" })
                }}
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center mr-1">
            <InlineLoading />
          </div>
        ) : (
          !row.isValid && (
            <div className="flex items-center">
              <Button
                small
                className="min-w-[50px]"
                disabled={disabledButton()}
                onClick={async () => {
                  setLoading(true)
                  await onUpdateRow({
                    ...row,
                    commodity: row.commodity || "",
                    unit: row.unit || "",
                    metadata: row.value || "",
                  })
                  setLoading(false)
                }}
              >
                Map
              </Button>
              <div
                className="flex items-center p-1 cursor-pointer ml-1 h-full"
                title="Discard"
                onClick={async () => {
                  setLoading(true)
                  await onDiscardRow(row)
                  setLoading(false)
                }}
              >
                <FontAwesomeIcon icon={faTrash} color="var(--support-01)" size="sm" />
              </div>
            </div>
          )
        )}

        {row.isValid && (
          <div className="flex items-center p-1">
            <FontAwesomeIcon icon={faCheck} color="var(--support-02)" />
          </div>
        )}
      </div>
    </div>
  )
}

export default SingleRow
